/**
 * API service for accounting functionality
 */

import {
  Business,
  UserTierInfo,
  Account,
  AccountTemplate,
  JournalEntry,
  JournalEntryCreateRequest,
  TrialBalance,
  BalanceSheet,
  BusinessDashboardData,
  BusinessUser,
  ApiResponse,
  BusinessesResponse,
  AccountsResponse,
  JournalEntriesResponse,
  TrialBalanceResponse,
  TaxRate,
  TaxRatesResponse,
  QuickExpense,
  QuickExpenseCreateRequest,
  QuickExpensesResponse,
  RecurringEntryTemplate,
  RecurringTemplateCreateRequest,
  RecurringTemplatesResponse,
  RecurringPreviewResponse,
  CSVUploadResponse,
  ColumnMapping,
  ImportedTransaction,
  BankImport,
  ApproveResponse,
  Receipt,
  ReceiptsResponse,
  BudgetItem,
  BudgetsResponse,
  BudgetSaveRequest,
  BudgetVsActualReport,
  BudgetVsActualResponse,
  Contact,
  ContactsResponse,
  ContactAgingResponse,
  Invoice,
  InvoicesResponse,
  InvoiceCreateRequest,
  AgingSummaryData,
  ContactAgingData,
  SalesTaxReport
} from '../types/accounting';

const API_BASE = process.env.REACT_APP_API_URL || '';

class AccountingApi {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T> & { data: T }> {
    const url = `${API_BASE}/api/accounting${endpoint}`;
    
    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Add authentication headers if available
    const token = localStorage.getItem('auth_token');
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(url, {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    });

    // Guard against non-JSON responses (e.g. HTML error pages from server)
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      const err = new Error('Server returned an unexpected response (status ' + response.status + '). The server may be temporarily unavailable.');
      (err as any).response = { status: response.status, data: { message: err.message } };
      throw err;
    }

    const result = await response.json();

    if (!response.ok) {
      const err = new Error(result.message || 'Request failed');
      (err as any).response = { status: response.status, data: result };
      throw err;
    }
    
    return {
      ...result,
      data: result.status === 'success' ? (result.data || result) : undefined
    };
  }

  // Authentication & Access
  async checkAccess(): Promise<ApiResponse<UserTierInfo> & { data: UserTierInfo }> {
    return this.request<UserTierInfo>('/auth/check-access', {
      method: 'POST'
    });
  }

  async checkBusinessAccess(businessId: number): Promise<ApiResponse<any>> {
    return this.request(`/auth/business-access/${businessId}`);
  }

  // Business Management
  async getBusinesses(): Promise<ApiResponse<BusinessesResponse> & { data: BusinessesResponse }> {
    return this.request<BusinessesResponse>('/business/');
  }

  async getBusiness(businessId: number): Promise<ApiResponse<{ business: Business }> & { data: { business: Business } }> {
    return this.request<{ business: Business }>(`/business/${businessId}`);
  }

  async createBusiness(businessData: Partial<Business>): Promise<ApiResponse<{ business: Business }> & { data: { business: Business } }> {
    return this.request<{ business: Business }>('/business/', {
      method: 'POST',
      body: JSON.stringify(businessData)
    });
  }

  async createSampleBusiness(): Promise<ApiResponse<{ business: Business }> & { data: { business: Business } }> {
    return this.request<{ business: Business }>('/business/sample', { method: 'POST' });
  }

  async deleteBusiness(businessId: number): Promise<ApiResponse<null>> {
    return this.request<null>(`/business/${businessId}`, { method: 'DELETE' });
  }

  async updateBusiness(businessId: number, businessData: Partial<Business>): Promise<ApiResponse<{ business: Business }> & { data: { business: Business } }> {
    return this.request<{ business: Business }>(`/business/${businessId}`, {
      method: 'PUT',
      body: JSON.stringify(businessData)
    });
  }

  async getBusinessDashboard(businessId: number): Promise<ApiResponse<{ dashboard: BusinessDashboardData }> & { data: { dashboard: BusinessDashboardData } }> {
    return this.request<{ dashboard: BusinessDashboardData }>(`/business/${businessId}/dashboard`);
  }

  // Business Users (Pro Business Plan)
  async getBusinessUsers(businessId: number): Promise<ApiResponse<{ users: BusinessUser[], count: number }>> {
    return this.request(`/business/${businessId}/users`);
  }

  async inviteUserToBusiness(businessId: number, email: string, role: string, permissions: any): Promise<ApiResponse<any>> {
    return this.request(`/business/${businessId}/invite`, {
      method: 'POST',
      body: JSON.stringify({ email, role, permissions })
    });
  }

  // Chart of Accounts
  async getAccountTemplates(): Promise<ApiResponse<{ templates: Record<string, AccountTemplate[]> }> & { data: { templates: Record<string, AccountTemplate[]> } }> {
    return this.request<{ templates: Record<string, AccountTemplate[]> }>('/accounts/templates');
  }

  async getBusinessAccounts(businessId: number): Promise<ApiResponse<AccountsResponse> & { data: AccountsResponse }> {
    return this.request<AccountsResponse>(`/accounts/business/${businessId}`);
  }

  async createAccount(businessId: number, accountData: Partial<Account>): Promise<ApiResponse<{ account: Account }> & { data: { account: Account } }> {
    return this.request<{ account: Account }>(`/accounts/business/${businessId}`, {
      method: 'POST',
      body: JSON.stringify(accountData)
    });
  }

  async createAccountFromTemplate(businessId: number, templateId: number, customName?: string): Promise<ApiResponse<{ account: Account }> & { data: { account: Account } }> {
    return this.request<{ account: Account }>(`/accounts/business/${businessId}/from-template`, {
      method: 'POST',
      body: JSON.stringify({ 
        template_id: templateId,
        account_name: customName 
      })
    });
  }

  async updateAccount(accountId: number, accountData: Partial<Account>): Promise<ApiResponse<{ account: Account }> & { data: { account: Account } }> {
    return this.request<{ account: Account }>(`/accounts/${accountId}`, {
      method: 'PUT',
      body: JSON.stringify(accountData)
    });
  }

  async getAccountBalance(accountId: number, asOfDate?: string): Promise<ApiResponse<any>> {
    const params = asOfDate ? `?as_of_date=${asOfDate}` : '';
    return this.request(`/accounts/${accountId}/balance${params}`);
  }

  // Journal Entries
  async getJournalEntries(businessId: number, page = 1, limit = 50): Promise<ApiResponse<JournalEntriesResponse> & { data: JournalEntriesResponse }> {
    return this.request<JournalEntriesResponse>(`/entries/business/${businessId}?page=${page}&limit=${limit}`);
  }

  async createJournalEntry(businessId: number, entryData: JournalEntryCreateRequest): Promise<ApiResponse<{ entry: JournalEntry }> & { data: { entry: JournalEntry } }> {
    return this.request<{ entry: JournalEntry }>(`/entries/business/${businessId}`, {
      method: 'POST',
      body: JSON.stringify(entryData)
    });
  }

  async postJournalEntry(entryId: number): Promise<ApiResponse<{ entry: JournalEntry }> & { data: { entry: JournalEntry } }> {
    return this.request<{ entry: JournalEntry }>(`/entries/${entryId}/post`, {
      method: 'POST'
    });
  }

  async voidJournalEntry(entryId: number): Promise<ApiResponse<{ entry: JournalEntry }> & { data: { entry: JournalEntry } }> {
    return this.request<{ entry: JournalEntry }>(`/entries/${entryId}/void`, {
      method: 'POST'
    });
  }

  async updateJournalEntry(entryId: number, data: JournalEntryCreateRequest): Promise<ApiResponse<{ entry: JournalEntry }> & { data: { entry: JournalEntry } }> {
    return this.request<{ entry: JournalEntry }>(`/entries/${entryId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteJournalEntry(entryId: number): Promise<ApiResponse<null>> {
    return this.request<null>(`/entries/${entryId}`, {
      method: 'DELETE'
    });
  }

  async searchJournalEntries(businessId: number, params: {
    q?: string; status?: string; entry_type?: string;
    start_date?: string; end_date?: string;
    account_id?: number; page?: number; limit?: number;
  }): Promise<ApiResponse<JournalEntriesResponse & { pagination: { total: number } }> & { data: JournalEntriesResponse & { pagination: { total: number } } }> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') searchParams.set(k, String(v)); });
    return this.request<JournalEntriesResponse & { pagination: { total: number } }>(`/entries/business/${businessId}/search?${searchParams}`);
  }

  // Financial Reports
  async getTrialBalance(businessId: number, asOfDate?: string): Promise<ApiResponse<TrialBalanceResponse> & { data: TrialBalanceResponse }> {
    const params = asOfDate ? `?as_of_date=${asOfDate}` : '';
    return this.request<TrialBalanceResponse>(`/reports/trial-balance/${businessId}${params}`);
  }

  async getBalanceSheet(businessId: number, asOfDate?: string): Promise<ApiResponse<{ balance_sheet: BalanceSheet, as_of_date: string }> & { data: { balance_sheet: BalanceSheet, as_of_date: string } }> {
    const params = asOfDate ? `?as_of_date=${asOfDate}` : '';
    return this.request<{ balance_sheet: BalanceSheet, as_of_date: string }>(`/reports/balance-sheet/${businessId}${params}`);
  }

  async getIncomeStatement(businessId: number, startDate?: string, endDate?: string): Promise<ApiResponse<{ income_statement: any, period: string }> & { data: { income_statement: any, period: string } }> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return this.request<{ income_statement: any, period: string }>(`/reports/income-statement/${businessId}${queryString}`);
  }

  async getGeneralLedger(businessId: number, accountId?: number, startDate?: string, endDate?: string, page = 1): Promise<ApiResponse<any> & { data: any }> {
    const params = new URLSearchParams();
    if (accountId) params.append('account_id', accountId.toString());
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    params.append('page', page.toString());
    return this.request<any>(`/reports/general-ledger/${businessId}?${params.toString()}`);
  }

  async getCashFlow(businessId: number, startDate?: string, endDate?: string): Promise<ApiResponse<any> & { data: any }> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return this.request<any>(`/reports/cash-flow/${businessId}${queryString}`);
  }

  // User invitation wrapper methods
  async inviteUser(businessId: number, invitationData: { email: string, role: string, message?: string }): Promise<ApiResponse<any>> {
    return this.inviteUserToBusiness(businessId, invitationData.email, invitationData.role, {});
  }

  // Payments & Subscriptions (Pro Business Plan)
  async createSetupIntent(): Promise<ApiResponse<{ client_secret: string, customer_id: string }> & { data: { client_secret: string, customer_id: string } }> {
    return this.request<{ client_secret: string, customer_id: string }>('/payments/pro-business/setup-intent', {
      method: 'POST'
    });
  }

  async createProBusinessSubscription(businessId: number, paymentMethodId: string, customerId: string): Promise<ApiResponse<any>> {
    return this.request('/payments/pro-business/subscribe', {
      method: 'POST',
      body: JSON.stringify({
        business_id: businessId,
        payment_method_id: paymentMethodId,
        customer_id: customerId
      })
    });
  }

  // Utility methods for common operations
  async validateJournalEntry(lines: JournalEntryCreateRequest['lines']): Promise<{ isValid: boolean; errors: string[] }> {
    const errors: string[] = [];
    
    // Check minimum lines
    if (lines.length < 2) {
      errors.push('At least two journal entry lines are required');
    }
    
    // Check balance
    const totalDebits = lines.reduce((sum, line) => sum + (line.debit_amount || 0), 0);
    const totalCredits = lines.reduce((sum, line) => sum + (line.credit_amount || 0), 0);
    
    if (Math.abs(totalDebits - totalCredits) > 0.01) {
      errors.push('Debits must equal credits');
    }
    
    // Check each line has either debit or credit (not both)
    lines.forEach((line, index) => {
      const hasDebit = (line.debit_amount || 0) > 0;
      const hasCredit = (line.credit_amount || 0) > 0;
      
      if (hasDebit && hasCredit) {
        errors.push(`Line ${index + 1}: Cannot have both debit and credit amounts`);
      }
      
      if (!hasDebit && !hasCredit) {
        errors.push(`Line ${index + 1}: Must have either a debit or credit amount`);
      }
      
      if (!line.account_id) {
        errors.push(`Line ${index + 1}: Account is required`);
      }
    });
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // ============== TAX RATES ==============

  async getTaxRates(businessId: number): Promise<ApiResponse<TaxRatesResponse> & { data: TaxRatesResponse }> {
    return this.request<TaxRatesResponse>(`/expenses/tax-rates/business/${businessId}`);
  }

  async createTaxRate(businessId: number, taxRateData: Partial<TaxRate>): Promise<ApiResponse<{ tax_rate: TaxRate }> & { data: { tax_rate: TaxRate } }> {
    return this.request<{ tax_rate: TaxRate }>(`/expenses/tax-rates/business/${businessId}`, {
      method: 'POST',
      body: JSON.stringify(taxRateData)
    });
  }

  async createDefaultTaxRates(businessId: number): Promise<ApiResponse<TaxRatesResponse> & { data: TaxRatesResponse }> {
    return this.request<TaxRatesResponse>(`/expenses/tax-rates/business/${businessId}/defaults`, {
      method: 'POST'
    });
  }

  // ============== QUICK EXPENSES ==============

  async getQuickExpenses(businessId: number, page = 1, limit = 50): Promise<ApiResponse<QuickExpensesResponse> & { data: QuickExpensesResponse }> {
    return this.request<QuickExpensesResponse>(`/expenses/business/${businessId}?page=${page}&limit=${limit}`);
  }

  async createQuickExpense(businessId: number, expenseData: QuickExpenseCreateRequest): Promise<ApiResponse<{ expense: QuickExpense, journal_entry_id: number }> & { data: { expense: QuickExpense, journal_entry_id: number } }> {
    return this.request<{ expense: QuickExpense, journal_entry_id: number }>(`/expenses/business/${businessId}`, {
      method: 'POST',
      body: JSON.stringify(expenseData)
    });
  }

  async deleteQuickExpense(expenseId: number): Promise<ApiResponse<void>> {
    return this.request(`/expenses/${expenseId}`, {
      method: 'DELETE'
    });
  }

  // ============== RECURRING ENTRIES ==============

  async getRecurringTemplates(businessId: number): Promise<ApiResponse<RecurringTemplatesResponse> & { data: RecurringTemplatesResponse }> {
    return this.request<RecurringTemplatesResponse>(`/recurring/business/${businessId}`);
  }

  async getRecurringTemplate(templateId: number): Promise<ApiResponse<{ template: RecurringEntryTemplate }> & { data: { template: RecurringEntryTemplate } }> {
    return this.request<{ template: RecurringEntryTemplate }>(`/recurring/${templateId}`);
  }

  async createRecurringTemplate(businessId: number, templateData: RecurringTemplateCreateRequest): Promise<ApiResponse<{ template: RecurringEntryTemplate }> & { data: { template: RecurringEntryTemplate } }> {
    return this.request<{ template: RecurringEntryTemplate }>(`/recurring/business/${businessId}`, {
      method: 'POST',
      body: JSON.stringify(templateData)
    });
  }

  async updateRecurringTemplate(templateId: number, templateData: Partial<RecurringTemplateCreateRequest>): Promise<ApiResponse<{ template: RecurringEntryTemplate }> & { data: { template: RecurringEntryTemplate } }> {
    return this.request<{ template: RecurringEntryTemplate }>(`/recurring/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(templateData)
    });
  }

  async deleteRecurringTemplate(templateId: number): Promise<ApiResponse<void>> {
    return this.request(`/recurring/${templateId}`, {
      method: 'DELETE'
    });
  }

  async pauseRecurringTemplate(templateId: number): Promise<ApiResponse<{ template: RecurringEntryTemplate }> & { data: { template: RecurringEntryTemplate } }> {
    return this.request<{ template: RecurringEntryTemplate }>(`/recurring/${templateId}/pause`, {
      method: 'POST'
    });
  }

  async resumeRecurringTemplate(templateId: number): Promise<ApiResponse<{ template: RecurringEntryTemplate }> & { data: { template: RecurringEntryTemplate } }> {
    return this.request<{ template: RecurringEntryTemplate }>(`/recurring/${templateId}/resume`, {
      method: 'POST'
    });
  }

  async skipRecurringOccurrence(templateId: number): Promise<ApiResponse<{ template: RecurringEntryTemplate }> & { data: { template: RecurringEntryTemplate } }> {
    return this.request<{ template: RecurringEntryTemplate }>(`/recurring/${templateId}/skip`, {
      method: 'POST'
    });
  }

  async previewRecurringEntries(templateId: number, count = 5): Promise<ApiResponse<RecurringPreviewResponse> & { data: RecurringPreviewResponse }> {
    return this.request<RecurringPreviewResponse>(`/recurring/${templateId}/preview?count=${count}`);
  }

  // ============== BANK IMPORTS ==============

  async uploadCSV(businessId: number, file: File): Promise<ApiResponse<CSVUploadResponse> & { data: CSVUploadResponse }> {
    const url = `${API_BASE}/api/accounting/business/${businessId}/upload-csv`;
    const formData = new FormData();
    formData.append('file', file);

    const headers: Record<string, string> = {};
    const token = localStorage.getItem('auth_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Do NOT set Content-Type — browser sets it with boundary for multipart

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    const result = await response.json();

    if (!response.ok) {
      const err = new Error(result.message || 'Upload failed');
      (err as any).response = { status: response.status, data: result };
      throw err;
    }

    return {
      ...result,
      data: result.status === 'success' ? (result.data || result) : undefined
    };
  }

  async mapColumns(businessId: number, importId: number, mapping: ColumnMapping): Promise<ApiResponse<void>> {
    return this.request(`/business/${businessId}/imports/${importId}/map-columns`, {
      method: 'POST',
      body: JSON.stringify({ mapping })
    });
  }

  async getImports(businessId: number): Promise<ApiResponse<{ imports: BankImport[] }> & { data: { imports: BankImport[] } }> {
    return this.request<{ imports: BankImport[] }>(`/business/${businessId}/imports`);
  }

  async getImportTransactions(businessId: number, importId: number): Promise<ApiResponse<{ transactions: ImportedTransaction[] }> & { data: { transactions: ImportedTransaction[] } }> {
    return this.request<{ transactions: ImportedTransaction[] }>(`/business/${businessId}/imports/${importId}/transactions`);
  }

  async categorizeTransactions(businessId: number, importId: number): Promise<ApiResponse<void>> {
    return this.request(`/business/${businessId}/imports/${importId}/categorize`, {
      method: 'POST'
    });
  }

  async approveTransactions(businessId: number, importId: number, transactionIds: number[]): Promise<ApiResponse<ApproveResponse> & { data: ApproveResponse }> {
    return this.request<ApproveResponse>(`/business/${businessId}/imports/${importId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ transaction_ids: transactionIds })
    });
  }

  // ============== RECEIPTS ==============

  async uploadReceipt(businessId: number, entryId: number, file: File): Promise<ApiResponse<{ receipt: Receipt }> & { data: { receipt: Receipt } }> {
    const url = `${API_BASE}/api/accounting/receipts/upload/${businessId}/${entryId}`;
    const formData = new FormData();
    formData.append('file', file);

    const headers: Record<string, string> = {};
    const token = localStorage.getItem('auth_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Do NOT set Content-Type - browser sets it with boundary for multipart

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      const err = new Error('Server returned an unexpected response (status ' + response.status + ').');
      (err as any).response = { status: response.status, data: { message: err.message } };
      throw err;
    }

    const result = await response.json();

    if (!response.ok) {
      const err = new Error(result.message || 'Upload failed');
      (err as any).response = { status: response.status, data: result };
      throw err;
    }

    return {
      ...result,
      data: result.status === 'success' ? (result.data || result) : undefined
    };
  }

  async getReceipts(businessId: number, entryId: number): Promise<ApiResponse<ReceiptsResponse> & { data: ReceiptsResponse }> {
    return this.request<ReceiptsResponse>(`/receipts/${businessId}/${entryId}`);
  }

  getReceiptUrl(receiptId: number): string {
    const token = localStorage.getItem('auth_token');
    const tokenParam = token ? '?token=' + encodeURIComponent(token) : '';
    return `${API_BASE}/api/accounting/receipts/file/${receiptId}${tokenParam}`;
  }

  async deleteReceipt(receiptId: number): Promise<ApiResponse<void>> {
    return this.request(`/receipts/${receiptId}`, {
      method: 'DELETE'
    });
  }

  // ============== BUDGET METHODS ==============

  async getBudgets(businessId: number, year?: number): Promise<ApiResponse<BudgetsResponse> & { data: BudgetsResponse }> {
    const params = year ? `?year=${year}` : '';
    return this.request<BudgetsResponse>(`/budgets/business/${businessId}${params}`);
  }

  async saveBudgets(businessId: number, data: BudgetSaveRequest): Promise<ApiResponse<any>> {
    return this.request(`/budgets/business/${businessId}`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async copyBudget(businessId: number, fromYear: number, toYear: number, adjustmentPct?: number): Promise<ApiResponse<any>> {
    return this.request(`/budgets/business/${businessId}/copy`, {
      method: 'POST',
      body: JSON.stringify({
        from_year: fromYear,
        to_year: toYear,
        adjustment_pct: adjustmentPct || 0
      })
    });
  }

  async deleteBudgetYear(businessId: number, year: number): Promise<ApiResponse<any>> {
    return this.request(`/budgets/business/${businessId}/delete-year?year=${year}`, {
      method: 'DELETE'
    });
  }

  async getBudgetVsActual(businessId: number, year?: number, month?: number): Promise<ApiResponse<BudgetVsActualResponse> & { data: BudgetVsActualResponse }> {
    const params = new URLSearchParams();
    if (year) params.append('year', String(year));
    if (month) params.append('month', String(month));
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return this.request<BudgetVsActualResponse>(`/reports/budget-vs-actual/${businessId}${queryString}`);
  }

  // ============== CONTACTS (CUSTOMERS/VENDORS) ==============

  async getContacts(businessId: number, type?: 'customer' | 'vendor' | 'both'): Promise<ApiResponse<ContactsResponse> & { data: ContactsResponse }> {
    const params = type ? `?type=${type}` : '';
    return this.request<ContactsResponse>(`/contacts/business/${businessId}${params}`);
  }

  async createContact(businessId: number, contactData: Partial<Contact>): Promise<ApiResponse<{ contact: Contact }> & { data: { contact: Contact } }> {
    return this.request<{ contact: Contact }>(`/contacts/business/${businessId}`, {
      method: 'POST',
      body: JSON.stringify(contactData)
    });
  }

  async updateContact(contactId: number, contactData: Partial<Contact>): Promise<ApiResponse<{ contact: Contact }> & { data: { contact: Contact } }> {
    return this.request<{ contact: Contact }>(`/contacts/${contactId}`, {
      method: 'PUT',
      body: JSON.stringify(contactData)
    });
  }

  async deleteContact(contactId: number): Promise<ApiResponse<void>> {
    return this.request(`/contacts/${contactId}`, {
      method: 'DELETE'
    });
  }

  async getContactAging(contactId: number): Promise<ApiResponse<ContactAgingResponse> & { data: ContactAgingResponse }> {
    return this.request<ContactAgingResponse>(`/contacts/${contactId}/aging`);
  }

  // ============== INVOICES ==============

  async getInvoices(businessId: number, status?: string, contactId?: number): Promise<ApiResponse<InvoicesResponse> & { data: InvoicesResponse }> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (contactId) params.append('contact_id', String(contactId));
    const queryString = params.toString() ? '?' + params.toString() : '';
    return this.request<InvoicesResponse>('/invoices/business/' + businessId + queryString);
  }

  async createInvoice(businessId: number, data: InvoiceCreateRequest): Promise<ApiResponse<{ invoice: Invoice }> & { data: { invoice: Invoice } }> {
    return this.request<{ invoice: Invoice }>('/invoices/business/' + businessId, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getInvoice(invoiceId: number): Promise<ApiResponse<{ invoice: Invoice }> & { data: { invoice: Invoice } }> {
    return this.request<{ invoice: Invoice }>('/invoices/' + invoiceId);
  }

  async updateInvoice(invoiceId: number, data: Partial<InvoiceCreateRequest>): Promise<ApiResponse<{ invoice: Invoice }> & { data: { invoice: Invoice } }> {
    return this.request<{ invoice: Invoice }>('/invoices/' + invoiceId, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async sendInvoice(invoiceId: number): Promise<ApiResponse<{ invoice: Invoice }> & { data: { invoice: Invoice } }> {
    return this.request<{ invoice: Invoice }>('/invoices/' + invoiceId + '/send', {
      method: 'POST'
    });
  }

  async recordPayment(invoiceId: number, paymentAccountId: number, amount?: number, paymentDate?: string): Promise<ApiResponse<{ invoice: Invoice, journal_entry: JournalEntry }> & { data: { invoice: Invoice, journal_entry: JournalEntry } }> {
    return this.request<{ invoice: Invoice, journal_entry: JournalEntry }>('/invoices/' + invoiceId + '/record-payment', {
      method: 'POST',
      body: JSON.stringify({ payment_account_id: paymentAccountId, amount, payment_date: paymentDate })
    });
  }

  async voidInvoice(invoiceId: number): Promise<ApiResponse<{ invoice: Invoice }> & { data: { invoice: Invoice } }> {
    return this.request<{ invoice: Invoice }>('/invoices/' + invoiceId + '/void', {
      method: 'POST'
    });
  }

  async deleteInvoice(invoiceId: number): Promise<ApiResponse<void>> {
    return this.request('/invoices/' + invoiceId, {
      method: 'DELETE'
    });
  }

  // ============== AR AGING ==============

  async getAgingSummary(businessId: number): Promise<ApiResponse<AgingSummaryData>> {
    return this.request<AgingSummaryData>('/contacts/business/' + businessId + '/aging-summary');
  }

  async getContactAgingDetail(contactId: number): Promise<ApiResponse<ContactAgingData>> {
    return this.request<ContactAgingData>('/contacts/' + contactId + '/aging');
  }

  // ============== SALES TAX REPORT ==============

  async getSalesTaxReport(businessId: number, startDate: string, endDate: string): Promise<ApiResponse<SalesTaxReport>> {
    return this.request<SalesTaxReport>(
      '/reports/sales-tax/' + businessId + '?start_date=' + startDate + '&end_date=' + endDate
    );
  }

  // Health check
  async healthCheck(): Promise<ApiResponse<any>> {
    return this.request('/health');
  }
}

export const accountingApi = new AccountingApi();
export default accountingApi;